import { useTranslation } from "react-i18next";

import { Section, SectionHeader, SectionTitle } from "@/blocks/Section";
import { SkeletonLabel } from "@/components/Skeleton";
import type { AnalyticsDisconnectedBy } from "@/graphql/generated/graphql";
import { useAnalyticsCallDetails } from "@/hooks/useAnalyticsCallDetails/useAnalyticsCallDetails";
import { useCall } from "@/hooks/useCall/useCall";

const DISCONNECTED_BY_TRANSLATION_KEY: Record<AnalyticsDisconnectedBy, string> =
  {
    AGENT: "call_context.disconnected_by_agent",
    EXTERNAL: "call_context.disconnected_by_external",
    SYSTEM: "call_context.disconnected_by_system",
  };

export function SectionDisconnectedBy() {
  const { t } = useTranslation();
  const { call } = useCall();

  const { disconnectedBy, loading, error } = useAnalyticsCallDetails(
    call?.id ?? ""
  );

  if (!call) {
    return null;
  }

  if (loading) {
    return (
      <Section data-test="disconnected-by-section">
        <SectionHeader>
          <SectionTitle>{t("call_context.disconnected_by")}</SectionTitle>
        </SectionHeader>
        <SkeletonLabel w={80} h={18} data-test="disconnected-by-skeleton" />
      </Section>
    );
  }

  // A missing/unauthorized/out-of-window call surfaces as a ResourceNotFoundException
  // in the response data (not an Apollo error), so `error` alone can't gate this - both
  // are treated the same: hide the section entirely rather than show an empty header.
  if (error || !disconnectedBy) {
    return null;
  }

  const displayValue = t(DISCONNECTED_BY_TRANSLATION_KEY[disconnectedBy]);

  return (
    <Section data-test="disconnected-by-section">
      <SectionHeader>
        <SectionTitle>{t("call_context.disconnected_by")}</SectionTitle>
      </SectionHeader>
      <span className="text-sm font-medium">{displayValue}</span>
    </Section>
  );
}
